import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader } from "@/components/admin/PageHeader";
import { MediaPicker } from "@/components/admin/MediaPicker";
import { adminBlogPostQuery, adminBlogPostsQuery } from "@/lib/blog/queries";
import { deleteBlogPostFn, saveBlogPostFn } from "@/lib/blog/blog.functions";
import { processCampaignFn, queuePostCampaignFn } from "@/lib/newsletter/newsletter.functions";
import { EMPTY_SEO, estimateReadingMinutes, slugify, type BlogPostInput, type BlogStatus } from "@/lib/blog/types";

export const Route = createFileRoute("/admin/blog/$id")({ component: BlogEditor });

const FIELD = "w-full border border-black/15 px-3 py-2 text-sm focus:border-black/50 focus:outline-none";
const LABEL = "text-[10px] font-semibold uppercase tracking-[0.2em] text-black/50";

const BLANK: BlogPostInput = {
  slug: "",
  title: "",
  excerpt: "",
  content: "",
  featuredImage: "",
  authorId: null,
  categoryId: null,
  tags: [],
  readingMinutes: 4,
  isFeatured: false,
  showOnHomepage: true,
  status: "draft",
  publishedAt: null,
  scheduledAt: null,
  seo: EMPTY_SEO,
};

function BlogEditor() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isNew = id === "new";
  const listQuery = useQuery(adminBlogPostsQuery());
  const postQuery = useQuery(adminBlogPostQuery(id));
  const [form, setForm] = useState<BlogPostInput>(BLANK);
  const [loaded, setLoaded] = useState(false);
  const [notifyFor, setNotifyFor] = useState<{ id: string; title: string } | null>(null);
  const [mediaOpen, setMediaOpen] = useState(false);

  useEffect(() => {
    if (isNew || loaded) return;
    const p = postQuery.data;
    if (!p) return;
    setForm({
      id: p.id,
      slug: p.slug,
      title: p.title,
      excerpt: p.excerpt,
      content: p.content,
      featuredImage: p.featuredImage,
      authorId: p.authorId,
      categoryId: p.categoryId,
      tags: p.tags,
      readingMinutes: p.readingMinutes,
      isFeatured: p.isFeatured,
      showOnHomepage: p.showOnHomepage,
      status: p.status,
      publishedAt: p.publishedAt,
      scheduledAt: p.scheduledAt,
      seo: p.seo,
    });
    setLoaded(true);
  }, [isNew, loaded, postQuery.data]);

  const set = <K extends keyof BlogPostInput>(key: K, value: BlogPostInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const save = useMutation({
    mutationFn: (input: BlogPostInput) => saveBlogPostFn({ data: input }),
    onSuccess: (saved, variables) => {
      void qc.invalidateQueries({ queryKey: ["blog"] });
      toast.success("Post saved");
      if (isNew && saved?.id) void navigate({ to: "/admin/blog/$id", params: { id: saved.id } });
      if (variables.status === "published" && saved?.id)
        setNotifyFor({ id: saved.id, title: saved.title });
    },
    onError: (e: Error) => toast.error(e.message || "Could not save the post"),
  });

  const notify = useMutation({
    mutationFn: async (postId: string) => {
      const queued = await queuePostCampaignFn({ data: { postId } });
      if (queued.campaignId)
        void processCampaignFn({ data: { campaignId: queued.campaignId } }).catch(() => undefined);
      return queued;
    },
    onSuccess: (r) => {
      setNotifyFor(null);
      void qc.invalidateQueries({ queryKey: ["campaigns"] });
      if (!r.campaignId) {
        toast.error(r.error || "Could not create the announcement campaign");
        return;
      }
      if (r.error) toast.warning(`Campaign created. ${r.error}`);
      else if (r.recipients === 0)
        toast.info("Campaign created — no active subscribers to notify yet");
      else toast.success(`Newsletter queued for ${r.recipients} subscriber(s)`);
    },
    onError: (e: Error) => {
      setNotifyFor(null);
      toast.error(e.message || "Could not create the announcement campaign");
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteBlogPostFn({ data: { id: form.id! } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["blog"] });
      toast.success("Post deleted");
      void navigate({ to: "/admin/blog" });
    },
    onError: (e: Error) => toast.error(e.message || "Could not delete the post"),
  });

  function submit(status: BlogStatus) {
    if (!form.title.trim()) return toast.error("Title is required");
    const slug = form.slug.trim() || slugify(form.title);
    save.mutate({
      ...form,
      slug,
      status,
      readingMinutes: form.readingMinutes || estimateReadingMinutes(form.content),
      seo: {
        ...form.seo,
        title: form.seo.title || `${form.title} — CB16 Journal`,
        description: form.seo.description || form.excerpt,
        canonical: form.seo.canonical || `https://cb16.in/blog/${slug}`,
        ogImage: form.seo.ogImage || form.featuredImage,
      },
    });
  }

  const categories = listQuery.data?.categories ?? [];
  const authors = listQuery.data?.authors ?? [];
  const tags = listQuery.data?.tags ?? [];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={isNew ? "New post" : form.title || "Edit post"}
        subtitle="Content, taxonomy, visibility and SEO"
        action={
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/blog" className="border border-black/15 px-3 py-2 text-[11px] uppercase tracking-[0.18em]">
              Back
            </Link>
            <button type="button" onClick={() => submit("draft")} disabled={save.isPending} className="border border-black/20 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em]">
              Save draft
            </button>
            <button type="button" onClick={() => submit("published")} disabled={save.isPending} className="bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white">
              Publish
            </button>
          </div>
        }
      />

      <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-4 border border-black/10 bg-white p-5">
          <div>
            <label className={LABEL}>Title</label>
            <input
              className={FIELD}
              value={form.title}
              onChange={(e) => {
                const title = e.target.value;
                setForm((f) => ({ ...f, title, slug: f.slug || slugify(title) }));
              }}
            />
          </div>
          <div>
            <label className={LABEL}>Slug</label>
            <input className={FIELD} value={form.slug} onChange={(e) => set("slug", slugify(e.target.value))} />
          </div>
          <div>
            <label className={LABEL}>Excerpt</label>
            <textarea className={FIELD} rows={3} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} />
          </div>
          <div>
            <label className={LABEL}>Content</label>
            <p className="mb-1 text-[11px] text-black/45">
              Supports ## headings, ### sub-headings, - lists, 1. numbered lists, &gt; quotes, **bold**, *italic* and [links](/path).
            </p>
            <textarea
              className={FIELD + " font-mono text-[13px] leading-6"}
              rows={24}
              value={form.content}
              onChange={(e) => set("content", e.target.value)}
            />
            <button
              type="button"
              className="mt-2 border border-black/15 px-3 py-1 text-[11px]"
              onClick={() => set("readingMinutes", estimateReadingMinutes(form.content))}
            >
              Recalculate reading time
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 border border-black/10 bg-white p-4">
            <p className={LABEL}>Publishing</p>
            <select className={FIELD} value={form.status} onChange={(e) => set("status", e.target.value as BlogStatus)}>
              <option value="draft">Draft</option>
              <option value="review">Review</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published</option>
              <option value="hidden">Hidden</option>
              <option value="archived">Archived</option>
            </select>
            <label className="flex items-center gap-2 text-[12px] text-black/70">
              <input type="checkbox" checked={form.isFeatured} onChange={(e) => set("isFeatured", e.target.checked)} />
              Featured article
            </label>
            <label className="flex items-center gap-2 text-[12px] text-black/70">
              <input type="checkbox" checked={form.showOnHomepage} onChange={(e) => set("showOnHomepage", e.target.checked)} />
              Show on homepage
            </label>
            <div>
              <label className={LABEL}>Scheduled at</label>
              <input
                type="datetime-local"
                className={FIELD}
                value={form.scheduledAt ? form.scheduledAt.slice(0, 16) : ""}
                onChange={(e) => set("scheduledAt", e.target.value ? new Date(e.target.value).toISOString() : null)}
              />
            </div>
            <div>
              <label className={LABEL}>Reading minutes</label>
              <input
                type="number"
                min={1}
                className={FIELD}
                value={form.readingMinutes}
                onChange={(e) => set("readingMinutes", Number(e.target.value) || 1)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-3 border border-black/10 bg-white p-4">
            <p className={LABEL}>Taxonomy</p>
            <select className={FIELD} value={form.categoryId ?? ""} onChange={(e) => set("categoryId", e.target.value || null)}>
              <option value="">No category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <select className={FIELD} value={form.authorId ?? ""} onChange={(e) => set("authorId", e.target.value || null)}>
              <option value="">No author</option>
              {authors.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => {
                const on = form.tags.includes(t.slug);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() =>
                      set("tags", on ? form.tags.filter((x) => x !== t.slug) : [...form.tags, t.slug])
                    }
                    className={
                      "border px-2 py-1 text-[11px] " +
                      (on ? "border-[#C6A15B] bg-[#C6A15B]/10 text-[#8a6f2e]" : "border-black/15 text-black/60")
                    }
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-3 border border-black/10 bg-white p-4">
            <p className={LABEL}>Featured image</p>
            <input className={FIELD} placeholder="https://…" value={form.featuredImage} onChange={(e) => set("featuredImage", e.target.value)} />
            <button
              type="button"
              onClick={() => setMediaOpen(true)}
              className="self-start border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest hover:border-[#C6A15B]"
            >
              Select from Media Library
            </button>
            {/* Selecting reuses the existing library file URL; nothing is re-uploaded. */}
            <MediaPicker
              open={mediaOpen}
              onClose={() => setMediaOpen(false)}
              onSelect={(asset) => { set("featuredImage", asset.url); setMediaOpen(false); }}
              title="Select featured image"
            />
            {form.featuredImage && <img src={form.featuredImage} alt="" className="aspect-video w-full object-cover" />}
          </div>

          <div className="flex flex-col gap-3 border border-black/10 bg-white p-4">
            <p className={LABEL}>SEO</p>
            <input className={FIELD} placeholder="Meta title" value={form.seo.title} onChange={(e) => set("seo", { ...form.seo, title: e.target.value })} />
            <textarea className={FIELD} rows={3} placeholder="Meta description" value={form.seo.description} onChange={(e) => set("seo", { ...form.seo, description: e.target.value })} />
            <input className={FIELD} placeholder="Keywords" value={form.seo.keywords} onChange={(e) => set("seo", { ...form.seo, keywords: e.target.value })} />
            <input className={FIELD} placeholder="Canonical URL" value={form.seo.canonical} onChange={(e) => set("seo", { ...form.seo, canonical: e.target.value })} />
            <input className={FIELD} placeholder="OG image URL" value={form.seo.ogImage} onChange={(e) => set("seo", { ...form.seo, ogImage: e.target.value })} />
          </div>

          {!isNew && (
            <button
              type="button"
              onClick={() => { if (confirm("Delete this post permanently?")) remove.mutate(); }}
              className="border border-red-300 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-red-700"
            >
              Delete post
            </button>
          )}
        </div>
      </div>

      {notifyFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md border border-black/10 bg-white p-6">
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#C6A15B]">
              Blog published
            </p>
            <h2 className="mt-2 text-lg text-black">Notify subscribers?</h2>
            <p className="mt-2 text-[13px] leading-6 text-black/60">
              Send “{notifyFor.title}” to every active blog subscriber. Publishing is already done —
              the campaign is queued in the background.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setNotifyFor(null)}
                className="border border-black/20 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em]"
              >
                No
              </button>
              <button
                type="button"
                disabled={notify.isPending}
                onClick={() => notify.mutate(notifyFor.id)}
                className="bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white disabled:opacity-50"
              >
                {notify.isPending ? "Queueing…" : "Yes, notify"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}